import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  BulkAssignProjectRoleDocument,
  BulkAssignProjectRoleMutation,
  ProjectManagementProjectRoleRoleEnum,
  ProjectRolesDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  CreateProjectRolesSelector,
  projectRoleInterface,
} from 'modules/project_management/components/wizards/AddProjectWizard/1_RolesSelectionStep/CreateProjectRolesSelector'

export interface ProjectIdWithPermissions {
  id: number
  canAssignOwners: boolean
}

export interface BulkAssignProjectRolesModalProps {
  projectIdWithPermissions: ProjectIdWithPermissions[]
  projectIds: number[]
  onClose: () => void
}

const BulkAssignProjectRolesModal = ({
  projectIdWithPermissions,
  projectIds,
  onClose,
}: BulkAssignProjectRolesModalProps) => {
  const [projectRoles, setProjectRoles] = useState<projectRoleInterface[]>([])
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeForSelectFragmentNoAccess[]>(
    []
  )

  const [bulkAssignProjectRole] = useMutation(BulkAssignProjectRoleDocument, {
    extractorFn: (data: BulkAssignProjectRoleMutation) =>
      data.projectManagementMutations.bulkAssignProjectmanagementProjectRole,
  })

  const onSubmitModal = async () => {
    const owners = projectRoles.flatMap((role) => (role.role === 'owner' ? role.employee.id : []))
    const editors = projectRoles.flatMap((role) => (role.role === 'editor' ? role.employee.id : []))
    const viewers = projectRoles.flatMap((role) => (role.role === 'viewer' ? role.employee.id : []))
    const response = await bulkAssignProjectRole({
      variables: { owners, editors, viewers, projectIds },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: ProjectRolesDocument,
          variables: {
            projectIds,
          },
        },
      ],
    })

    const handled = handleResult(response, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('project_management.toasts.assign_roles_multiple_projects_success'),
      })

      track(trackingEvents.projectManagement.bulkActions.roleProjectsAssignment.eventSubmit, {
        owners_assigned_count: owners.length,
        editors_assigned_count: editors.length,
        viewers_assigned_count: viewers.length,
        projects_assigned_count: projectIds.length,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }
    onClose()
  }

  const disabledEmployeeIds = projectRoles.flatMap((role) =>
    role.employee ? [role.employee.id] : []
  )

  const disabledSubmitButton = projectRoles.length === 0

  const onSelectEmployees = (newEmployees: EmployeeForSelectFragmentNoAccess[]) => {
    setSelectedEmployees(newEmployees)
  }

  const onUpdateRow = (
    employee: EmployeeForSelectFragmentNoAccess,
    role: ProjectManagementProjectRoleRoleEnum
  ) => {
    const index = projectRoles.findIndex((pr) => pr.employee.id === employee.id)

    if (index !== -1) {
      setProjectRoles((prev) => prev.map((pr, i) => (i === index ? { ...pr, role } : pr)))
    } else {
      setProjectRoles((prev) => [
        ...prev,
        {
          role,
          employee,
        },
      ])
    }
  }

  const onDeleteRow = (employee: EmployeeForSelectFragmentNoAccess) => {
    setProjectRoles((prev) => prev.filter((pr) => pr.employee.id !== employee.id))
  }

  const isProjectOwner = projectIdWithPermissions
    .filter((item) => projectIds.includes(item.id))
    .every((item) => item.canAssignOwners)

  return (
    <Modal
      illustration={'add-user'}
      title={i18n.t('project_management.modals.bulk_assign_roles.title')}
      description={i18n.t('project_management.modals.bulk_assign_roles.description', {
        projects_number: projectIds.length,
      })}
      onClose={onClose}
      onSubmit={onSubmitModal}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={disabledSubmitButton} submit>
            {i18n.t('common.add')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <CreateProjectRolesSelector
          onUpdateRow={onUpdateRow}
          onDeleteRow={onDeleteRow}
          projectRoles={projectRoles}
          isProjectOwner={isProjectOwner}
          excludeEmployeeIds={disabledEmployeeIds}
          onSelectEmployees={onSelectEmployees}
          selectedEmployees={selectedEmployees}
          setSelectedEmployees={setSelectedEmployees}
        />
      </Box>
    </Modal>
  )
}

export default observer(BulkAssignProjectRolesModal)
